import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building2, 
  Plus, 
  Search,
  Users,
  Store,
  Package,
  Settings,
  MoreVertical,
  Check,
  X,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  UserPlus,
  Key,
  Mail,
  User,
  Menu,
  LayoutDashboard,
  ShoppingCart,
  Wallet,
  MessageSquare,
  Bell,
  BarChart3,
  Sparkles,
  History,
  Loader2,
  Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Separator } from '../components/ui/separator';
import { getErrorMessage } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AssociationModules {
  groupage: boolean;
  supplier_directory: boolean;
  ai_chat: boolean;
  analytics: boolean;
  stock_management: boolean;
}

interface Association {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  email?: string;
  city?: string;
  province?: string;
  modules: AssociationModules;
  max_members: number;
  max_sellers: number;
  is_active: boolean;
  subscription_type: string;
  created_at: string;
  stats?: {
    members: number;
    sellers: number;
    active_groupages: number;
  };
}

const AdminAssociationsPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const [associations, setAssociations] = useState<Association[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showModulesDialog, setShowModulesDialog] = useState(false);
  const [showCreateAdminDialog, setShowCreateAdminDialog] = useState(false);
  const [showMenuConfigDialog, setShowMenuConfigDialog] = useState(false);
  const [showTenantConfigDialog, setShowTenantConfigDialog] = useState(false);
  const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [menuConfig, setMenuConfig] = useState<any>(null);
  const [savingMenuConfig, setSavingMenuConfig] = useState(false);
  const [tenantConfig, setTenantConfig] = useState<any>(null);
  const [savingTenantConfig, setSavingTenantConfig] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    city: '',
    province: '',
    vat_number: '',
    max_members: 1000,
    max_sellers: 100,
    subscription_type: 'basic'
  });
  
  // Admin user form
  const [adminFormData, setAdminFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: ''
  });
  
  const [modulesData, setModulesData] = useState<AssociationModules>({
    groupage: true,
    supplier_directory: false,
    ai_chat: false,
    analytics: true,
    stock_management: false
  });

  useEffect(() => {
    if (isAdmin) {
      fetchAssociations();
    }
  }, [isAdmin]);

  const fetchAssociations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterActive !== null) params.append('is_active', String(filterActive));
      
      const res = await axios.get(`${API}/associations?${params}`);
      setAssociations(res.data.associations);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssociation = async () => {
    try {
      const res = await axios.post(`${API}/associations`, formData);
      toast.success('Associazione creata con successo');
      setShowCreateDialog(false);
      resetForm();
      fetchAssociations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateAssociation = async () => {
    if (!selectedAssociation) return;
    
    try {
      await axios.put(`${API}/associations/${selectedAssociation.id}`, formData);
      toast.success('Associazione aggiornata');
      setShowEditDialog(false);
      fetchAssociations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateModules = async () => {
    if (!selectedAssociation) return;
    
    try {
      await axios.patch(`${API}/associations/${selectedAssociation.id}/modules`, modulesData);
      toast.success('Moduli aggiornati');
      setShowModulesDialog(false);
      fetchAssociations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async (association: Association) => {
    try {
      await axios.put(`${API}/associations/${association.id}`, {
        is_active: !association.is_active
      });
      toast.success(association.is_active ? 'Associazione disattivata' : 'Associazione attivata');
      fetchAssociations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openCreateAdminDialog = (association: Association) => {
    setSelectedAssociation(association);
    setAdminFormData({
      name: '',
      email: '',
      password: '',
      company_name: `Admin ${association.name}`
    });
    setShowCreateAdminDialog(true);
  };

  const handleCreateAdmin = async () => {
    if (!selectedAssociation) return;
    
    // Validazione
    if (!adminFormData.name || !adminFormData.email || !adminFormData.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    if (adminFormData.password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    
    setCreatingAdmin(true);
    
    try {
      const res = await axios.post(`${API}/associations/${selectedAssociation.id}/create-admin`, {
        name: adminFormData.name,
        email: adminFormData.email,
        password: adminFormData.password,
        company_name: adminFormData.company_name || `Admin ${selectedAssociation.name}`
      });
      
      toast.success(`Admin creato! Email: ${adminFormData.email}`);
      setShowCreateAdminDialog(false);
      setAdminFormData({ name: '', email: '', password: '', company_name: '' });
      fetchAssociations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCreatingAdmin(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setAdminFormData({ ...adminFormData, password });
  };

  // Menu Config Functions
  const openMenuConfigDialog = async (association: Association) => {
    setSelectedAssociation(association);
    try {
      const res = await axios.get(`${API}/associations/${association.id}/menu-config`);
      setMenuConfig(res.data.menu_config);
      setShowMenuConfigDialog(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSaveMenuConfig = async () => {
    if (!selectedAssociation || !menuConfig) return;
    
    setSavingMenuConfig(true);
    try {
      await axios.put(`${API}/associations/${selectedAssociation.id}/menu-config`, menuConfig);
      toast.success('Configurazione menu salvata');
      setShowMenuConfigDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingMenuConfig(false);
    }
  };

  const handleResetMenuConfig = async () => {
    if (!selectedAssociation) return;
    
    try {
      const res = await axios.post(`${API}/associations/${selectedAssociation.id}/menu-config/reset`);
      setMenuConfig(res.data.menu_config);
      toast.success('Configurazione menu resettata ai valori default');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const toggleMenuItem = (menuType: 'admin_menu' | 'member_menu', itemKey: string) => {
    setMenuConfig((prev: any) => ({
      ...prev,
      [menuType]: {
        ...prev[menuType],
        [itemKey]: {
          ...prev[menuType][itemKey],
          visible: !prev[menuType][itemKey]?.visible
        }
      }
    }));
  };

  const updateMenuItemLabel = (menuType: 'admin_menu' | 'member_menu', itemKey: string, label: string) => {
    setMenuConfig((prev: any) => ({
      ...prev,
      [menuType]: {
        ...prev[menuType],
        [itemKey]: {
          ...prev[menuType][itemKey],
          label: label || null
        }
      }
    }));
  };

  // === TENANT CONFIG ===
  const openTenantConfig = async (assoc: Association) => {
    setSelectedAssociation(assoc);
    try {
      const res = await axios.get(`${API}/associations/${assoc.id}/tenant-config`);
      setTenantConfig(res.data.tenant_config || {});
    } catch {
      // Default config
      setTenantConfig({
        groupage_enabled: true, directory_fornitori_enabled: true, ai_chat_enabled: false,
        analytics_enabled: true, stock_management_enabled: false, delivery_sites_enabled: false,
        delivery_zones_enabled: false, delivery_routes_enabled: false, billing_enabled: false,
        team_management_enabled: true, extensions_enabled: false,
        catalog_access_mode: 'request', offering_type: 'products',
        catalog_visibility: 'private', public_profiles_enabled: false,
        custom_labels: { suppliers_label: null, members_label: null, catalog_label: null, orders_label: null }
      });
    }
    setShowTenantConfigDialog(true);
  };

  const handleSaveTenantConfig = async () => {
    if (!selectedAssociation || !tenantConfig) return;
    setSavingTenantConfig(true);
    try {
      await axios.put(`${API}/associations/${selectedAssociation.id}/tenant-config`, tenantConfig);
      toast.success('Configurazione tenant salvata');
      setShowTenantConfigDialog(false);
      fetchAssociations();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingTenantConfig(false);
    }
  };

  const updateTenantField = (field: string, value: any) => {
    setTenantConfig((prev: any) => ({ ...prev, [field]: value }));
  };

  const updateTenantLabel = (field: string, value: string) => {
    setTenantConfig((prev: any) => ({
      ...prev,
      custom_labels: { ...(prev.custom_labels || {}), [field]: value || null }
    }));
  };


  // Menu items with labels and icons
  const MENU_ITEMS = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'groupage', label: 'Groupage', icon: Package },
    { key: 'gestione_associazione', label: 'Gestione Associazione', icon: Building2 },
    { key: 'gestione_associati', label: 'Gestione Associati', icon: Users },
    { key: 'richieste_membership', label: 'Richieste Membership', icon: UserPlus },
    { key: 'fornitori_associazione', label: 'Fornitori Associazione', icon: Store },
    { key: 'impostazioni_associazione', label: 'Impostazioni Associazione', icon: Settings },
    { key: 'ordini', label: 'Ordini', icon: ShoppingCart },
    { key: 'directory_fornitori', label: 'Directory Fornitori', icon: Building2 },
    { key: 'fornitori', label: 'Fornitori', icon: Store },
    { key: 'pagamenti', label: 'Pagamenti', icon: Wallet },
    { key: 'storico_transazioni', label: 'Storico Transazioni', icon: History },
    { key: 'messaggi', label: 'Messaggi', icon: MessageSquare },
    { key: 'notifiche', label: 'Notifiche', icon: Bell },
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'report', label: 'Report', icon: BarChart3 },
    { key: 'team', label: 'Team', icon: Users },
    { key: 'estensioni', label: 'Estensioni', icon: Sparkles },
    { key: 'impostazioni', label: 'Impostazioni', icon: Settings }
  ];

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      email: '',
      phone: '',
      website: '',
      city: '',
      province: '',
      vat_number: '',
      max_members: 1000,
      max_sellers: 100,
      subscription_type: 'basic'
    });
  };

  const openEditDialog = (association: Association) => {
    setSelectedAssociation(association);
    setFormData({
      name: association.name,
      slug: association.slug,
      description: association.description || '',
      email: association.email || '',
      phone: '',
      website: '',
      city: association.city || '',
      province: association.province || '',
      vat_number: '',
      max_members: association.max_members,
      max_sellers: association.max_sellers,
      subscription_type: association.subscription_type
    });
    setShowEditDialog(true);
  };

  const openModulesDialog = (association: Association) => {
    setSelectedAssociation(association);
    setModulesData(association.modules);
    setShowModulesDialog(true);
  };

  const getSubscriptionBadge = (type: string) => {
    const colors: Record<string, string> = {
      trial: 'bg-yellow-100 text-yellow-800',
      basic: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-green-100 text-green-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Accesso riservato al Super Admin</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-associations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Gestione Associazioni</h1>
          <p className="text-muted-foreground">
            Multi-tenant white-label per associazioni
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-association-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuova Associazione
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale Associazioni</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{associations.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Attive</CardTitle>
            <Check className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {associations.filter(a => a.is_active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale Membri</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {associations.reduce((acc, a) => acc + (a.stats?.members || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Groupage Attivi</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {associations.reduce((acc, a) => acc + (a.stats?.active_groupages || 0), 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome o slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchAssociations()}
            className="pl-10"
          />
        </div>
        <Select 
          value={filterActive === null ? 'all' : String(filterActive)}
          onValueChange={(v) => setFilterActive(v === 'all' ? null : v === 'true')}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="true">Attive</SelectItem>
            <SelectItem value="false">Inattive</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchAssociations}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Associations List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin" />
        </div>
      ) : associations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nessuna associazione trovata</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowCreateDialog(true)}
            >
              Crea la prima associazione
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {associations.map((association) => (
            <Card key={association.id} className={!association.is_active ? 'opacity-60' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {association.logo_url ? (
                        <img src={association.logo_url} alt="" className="h-6 w-6 rounded" />
                      ) : (
                        <Building2 className="h-5 w-5" />
                      )}
                      {association.name}
                    </CardTitle>
                    <CardDescription>
                      {association.slug}.ops.online
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openCreateAdminDialog(association)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Crea Admin
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => openEditDialog(association)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Modifica
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openModulesDialog(association)}>
                        <Settings className="h-4 w-4 mr-2" />
                        Gestisci Moduli
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openMenuConfigDialog(association)}>
                        <Menu className="h-4 w-4 mr-2" />
                        Configura Menu
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openTenantConfig(association)}>
                        <Settings className="h-4 w-4 mr-2" />
                        Configura Feature Tenant
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleToggleActive(association)}>
                        {association.is_active ? (
                          <>
                            <X className="h-4 w-4 mr-2" />
                            Disattiva
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Attiva
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className={getSubscriptionBadge(association.subscription_type)}>
                    {association.subscription_type}
                  </Badge>
                  {association.is_active ? (
                    <Badge variant="outline" className="border-green-500 text-green-600">
                      Attiva
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-red-500 text-red-600">
                      Inattiva
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">{association.stats?.members || 0}</div>
                    <div className="text-xs text-muted-foreground">Membri</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{association.stats?.sellers || 0}</div>
                    <div className="text-xs text-muted-foreground">Seller</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{association.stats?.active_groupages || 0}</div>
                    <div className="text-xs text-muted-foreground">Groupage</div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <div className="text-xs text-muted-foreground mb-2">Moduli attivi:</div>
                  <div className="flex flex-wrap gap-1">
                    {association.modules?.groupage && (
                      <Badge variant="secondary" className="text-xs">Groupage</Badge>
                    )}
                    {association.modules?.supplier_directory && (
                      <Badge variant="secondary" className="text-xs">Directory</Badge>
                    )}
                    {association.modules?.ai_chat && (
                      <Badge variant="secondary" className="text-xs">AI Chat</Badge>
                    )}
                    {association.modules?.analytics && (
                      <Badge variant="secondary" className="text-xs">Analytics</Badge>
                    )}
                    {association.modules?.stock_management && (
                      <Badge variant="secondary" className="text-xs">Stock</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Associazione</DialogTitle>
            <DialogDescription>
              Crea un nuovo tenant white-label per un'associazione
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Associazione *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Es. FIDA"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug (sottodominio) *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                  placeholder="Es. fida"
                />
                <p className="text-xs text-muted-foreground">
                  {formData.slug || 'slug'}.ops.online
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Breve descrizione dell'associazione"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="info@associazione.it"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subscription_type">Piano</Label>
                <Select 
                  value={formData.subscription_type}
                  onValueChange={(v) => setFormData({...formData, subscription_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">Città</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="province">Provincia</Label>
                <Input
                  id="province"
                  value={formData.province}
                  onChange={(e) => setFormData({...formData, province: e.target.value})}
                  maxLength={2}
                  placeholder="Es. RM"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max_members">Max Membri</Label>
                <Input
                  id="max_members"
                  type="number"
                  value={formData.max_members}
                  onChange={(e) => setFormData({...formData, max_members: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_sellers">Max Seller</Label>
                <Input
                  id="max_sellers"
                  type="number"
                  value={formData.max_sellers}
                  onChange={(e) => setFormData({...formData, max_sellers: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateAssociation} disabled={!formData.name || !formData.slug}>
              Crea Associazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Associazione</DialogTitle>
            <DialogDescription>
              Modifica i dettagli dell'associazione
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome Associazione *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input value={formData.slug} disabled />
                <p className="text-xs text-muted-foreground">Lo slug non può essere modificato</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Descrizione</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-subscription_type">Piano</Label>
                <Select 
                  value={formData.subscription_type}
                  onValueChange={(v) => setFormData({...formData, subscription_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-max_members">Max Membri</Label>
                <Input
                  id="edit-max_members"
                  type="number"
                  value={formData.max_members}
                  onChange={(e) => setFormData({...formData, max_members: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-max_sellers">Max Seller</Label>
                <Input
                  id="edit-max_sellers"
                  type="number"
                  value={formData.max_sellers}
                  onChange={(e) => setFormData({...formData, max_sellers: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateAssociation}>
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modules Dialog */}
      <Dialog open={showModulesDialog} onOpenChange={setShowModulesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gestione Moduli</DialogTitle>
            <DialogDescription>
              Attiva o disattiva i moduli per {selectedAssociation?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Groupage</Label>
                <p className="text-sm text-muted-foreground">
                  Acquisti collettivi per i membri
                </p>
              </div>
              <Switch
                checked={modulesData.groupage}
                onCheckedChange={(v) => setModulesData({...modulesData, groupage: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Directory Fornitori</Label>
                <p className="text-sm text-muted-foreground">
                  Accesso al repository fornitori esterni
                </p>
              </div>
              <Switch
                checked={modulesData.supplier_directory}
                onCheckedChange={(v) => setModulesData({...modulesData, supplier_directory: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>AI Chat</Label>
                <p className="text-sm text-muted-foreground">
                  Chatbot AI per assistenza
                </p>
              </div>
              <Switch
                checked={modulesData.ai_chat}
                onCheckedChange={(v) => setModulesData({...modulesData, ai_chat: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Analytics</Label>
                <p className="text-sm text-muted-foreground">
                  Dashboard e reportistica
                </p>
              </div>
              <Switch
                checked={modulesData.analytics}
                onCheckedChange={(v) => setModulesData({...modulesData, analytics: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Gestione Stock</Label>
                <p className="text-sm text-muted-foreground">
                  Tracciamento inventario
                </p>
              </div>
              <Switch
                checked={modulesData.stock_management}
                onCheckedChange={(v) => setModulesData({...modulesData, stock_management: v})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModulesDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateModules}>
              Salva Moduli
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Config Dialog */}
      <Dialog open={showMenuConfigDialog} onOpenChange={setShowMenuConfigDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Menu className="h-5 w-5" />
              Configurazione Menu - {selectedAssociation?.name}
            </DialogTitle>
            <DialogDescription>
              Configura le voci di menu visibili per Admin e Membri dell'associazione
            </DialogDescription>
          </DialogHeader>
          
          {menuConfig && (
            <Tabs defaultValue="admin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="admin">Menu Admin</TabsTrigger>
                <TabsTrigger value="member">Menu Membro</TabsTrigger>
              </TabsList>
              
              <TabsContent value="admin" className="space-y-4 mt-4">
                <div className="text-sm text-muted-foreground mb-4">
                  Voci di menu per gli amministratori — toggle on/off e personalizza l'etichetta
                </div>
                <div className="space-y-2">
                  {MENU_ITEMS.map((item) => {
                    const IconComponent = item.icon;
                    const isVisible = menuConfig.admin_menu?.[item.key]?.visible !== false;
                    const customLabel = menuConfig.admin_menu?.[item.key]?.label || '';
                    return (
                      <div key={item.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isVisible ? 'bg-primary/5 border-primary/30' : 'bg-muted/30 border-transparent opacity-60'}`}>
                        <Switch checked={isVisible} onCheckedChange={() => toggleMenuItem('admin_menu', item.key)} />
                        <IconComponent className={`h-4 w-4 shrink-0 ${isVisible ? 'text-primary' : 'text-muted-foreground'}`} />
                        <div className="flex-1 flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-28 shrink-0">{item.label}</span>
                          <Input
                            value={customLabel}
                            onChange={(e) => updateMenuItemLabel('admin_menu', item.key, e.target.value)}
                            placeholder={item.label}
                            className="h-7 text-sm"
                            disabled={!isVisible}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="member" className="space-y-4 mt-4">
                <div className="text-sm text-muted-foreground mb-4">
                  Voci di menu per i membri/associati — toggle on/off e personalizza l'etichetta
                </div>
                <div className="space-y-2">
                  {MENU_ITEMS.map((item) => {
                    const IconComponent = item.icon;
                    const isVisible = menuConfig.member_menu?.[item.key]?.visible !== false;
                    const customLabel = menuConfig.member_menu?.[item.key]?.label || '';
                    return (
                      <div key={item.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isVisible ? 'bg-primary/5 border-primary/30' : 'bg-muted/30 border-transparent opacity-60'}`}>
                        <Switch checked={isVisible} onCheckedChange={() => toggleMenuItem('member_menu', item.key)} />
                        <IconComponent className={`h-4 w-4 shrink-0 ${isVisible ? 'text-primary' : 'text-muted-foreground'}`} />
                        <div className="flex-1 flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-28 shrink-0">{item.label}</span>
                          <Input
                            value={customLabel}
                            onChange={(e) => updateMenuItemLabel('member_menu', item.key, e.target.value)}
                            placeholder={item.label}
                            className="h-7 text-sm"
                            disabled={!isVisible}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="flex justify-between mt-6">
            <Button variant="outline" onClick={handleResetMenuConfig}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset Default
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowMenuConfigDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleSaveMenuConfig} disabled={savingMenuConfig}>
                {savingMenuConfig ? 'Salvataggio...' : 'Salva Configurazione'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdminDialog} onOpenChange={setShowCreateAdminDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Crea Admin Associazione
            </DialogTitle>
            <DialogDescription>
              Crea un nuovo utente amministratore per <strong>{selectedAssociation?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Nome Completo *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="admin-name"
                  value={adminFormData.name}
                  onChange={(e) => setAdminFormData({...adminFormData, name: e.target.value})}
                  placeholder="Mario Rossi"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="admin-email"
                  type="email"
                  value={adminFormData.email}
                  onChange={(e) => setAdminFormData({...adminFormData, email: e.target.value})}
                  placeholder="admin@associazione.it"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-password">Password *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="admin-password"
                    type="text"
                    value={adminFormData.password}
                    onChange={(e) => setAdminFormData({...adminFormData, password: e.target.value})}
                    placeholder="Min. 8 caratteri"
                    className="pl-10 font-mono"
                  />
                </div>
                <Button type="button" variant="outline" onClick={generatePassword}>
                  Genera
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Minimo 8 caratteri. Comunica questa password all'admin.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-company">Nome Azienda</Label>
              <Input
                id="admin-company"
                value={adminFormData.company_name}
                onChange={(e) => setAdminFormData({...adminFormData, company_name: e.target.value})}
                placeholder={`Admin ${selectedAssociation?.name || 'Associazione'}`}
              />
            </div>

            <Separator />

            <div className="bg-muted/50 p-3 rounded-lg text-sm">
              <p className="font-medium mb-1">Riepilogo credenziali:</p>
              <p><strong>Email:</strong> {adminFormData.email || '-'}</p>
              <p><strong>Password:</strong> {adminFormData.password || '-'}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdminDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateAdmin} 
              disabled={!adminFormData.name || !adminFormData.email || !adminFormData.password || creatingAdmin}
            >
              {creatingAdmin ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Crea Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Configurazione Feature Tenant */}
      <Dialog open={showTenantConfigDialog} onOpenChange={setShowTenantConfigDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurazione Feature — {selectedAssociation?.name}
            </DialogTitle>
            <DialogDescription>
              Attiva/disattiva le funzionalità e personalizza il tenant
            </DialogDescription>
          </DialogHeader>

          {tenantConfig && (
            <div className="space-y-6 py-2">
              
              {/* Feature Toggle */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Funzionalità</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'groupage_enabled', label: 'Groupage', desc: 'Acquisto collettivo tra membri' },
                    { key: 'directory_fornitori_enabled', label: 'Directory Fornitori', desc: 'Accesso alla directory' },
                    { key: 'ai_chat_enabled', label: 'Chat AI', desc: 'Chatbot intelligenza artificiale' },
                    { key: 'analytics_enabled', label: 'Analytics', desc: 'Dashboard statistiche' },
                    { key: 'stock_management_enabled', label: 'Gestione Stock', desc: 'Monitoraggio giacenze' },
                    { key: 'delivery_sites_enabled', label: 'Siti di Consegna', desc: 'Gestione indirizzi multipli' },
                    { key: 'delivery_zones_enabled', label: 'Zone di Consegna', desc: 'Aree e costi spedizione' },
                    { key: 'delivery_routes_enabled', label: 'Percorsi Consegna', desc: 'Ottimizzazione giri' },
                    { key: 'billing_enabled', label: 'Fatturazione', desc: 'Fatture e pagamenti' },
                    { key: 'team_management_enabled', label: 'Gestione Team', desc: 'Multi-utente e ruoli' },
                    { key: 'extensions_enabled', label: 'Store Estensioni', desc: 'Estensioni premium' },
                    { key: 'public_profiles_enabled', label: 'Profili Pubblici', desc: 'Pagina /nomescelto per membri' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                      <Switch
                        checked={tenantConfig[item.key] ?? false}
                        onCheckedChange={(v) => updateTenantField(item.key, v)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Logica accesso catalogo */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Logica Accesso</h3>
                <div className="space-y-2">
                  <Label>Modalità accesso catalogo</Label>
                  <Select value={tenantConfig.catalog_access_mode || 'request'} onValueChange={(v) => updateTenantField('catalog_access_mode', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="request">Richiesta accesso (il buyer deve essere approvato)</SelectItem>
                      <SelectItem value="direct">Accesso diretto (tutti vedono il catalogo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Visibilità catalogo</Label>
                  <Select value={tenantConfig.catalog_visibility || 'private'} onValueChange={(v) => updateTenantField('catalog_visibility', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Privato (solo dopo relazione attiva)</SelectItem>
                      <SelectItem value="public_no_prices">Pubblico senza prezzi</SelectItem>
                      <SelectItem value="public_with_prices">Pubblico con prezzi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* Tipo offerta */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Tipo Offerta</h3>
                <Select value={tenantConfig.offering_type || 'products'} onValueChange={(v) => updateTenantField('offering_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="products">Solo Prodotti</SelectItem>
                    <SelectItem value="services">Solo Servizi</SelectItem>
                    <SelectItem value="both">Prodotti e Servizi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Etichette personalizzate */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Etichette Personalizzate</h3>
                <p className="text-xs text-muted-foreground">Lascia vuoto per usare il nome predefinito</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Fornitori (default: "Fornitori")</Label>
                    <Input value={tenantConfig.custom_labels?.suppliers_label || ''} onChange={(e) => updateTenantLabel('suppliers_label', e.target.value)} placeholder="Fornitori" />
                  </div>
                  <div>
                    <Label className="text-xs">Associati (default: "Associati")</Label>
                    <Input value={tenantConfig.custom_labels?.members_label || ''} onChange={(e) => updateTenantLabel('members_label', e.target.value)} placeholder="Associati" />
                  </div>
                  <div>
                    <Label className="text-xs">Catalogo (default: "Catalogo")</Label>
                    <Input value={tenantConfig.custom_labels?.catalog_label || ''} onChange={(e) => updateTenantLabel('catalog_label', e.target.value)} placeholder="Catalogo" />
                  </div>
                  <div>
                    <Label className="text-xs">Ordini (default: "Ordini")</Label>
                    <Input value={tenantConfig.custom_labels?.orders_label || ''} onChange={(e) => updateTenantLabel('orders_label', e.target.value)} placeholder="Ordini" />
                  </div>
                </div>
                
                <h4 className="font-medium text-sm pt-2">Pagina Directory</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Titolo pagina (default: "Directory Fornitori")</Label>
                    <Input value={tenantConfig.custom_labels?.directory_title || ''} onChange={(e) => updateTenantLabel('directory_title', e.target.value)} placeholder="Directory Fornitori" />
                  </div>
                  <div>
                    <Label className="text-xs">Sottotitolo pagina</Label>
                    <Input value={tenantConfig.custom_labels?.directory_subtitle || ''} onChange={(e) => updateTenantLabel('directory_subtitle', e.target.value)} placeholder="Trova fornitori e attiva collaborazioni" />
                  </div>
                  <div>
                    <Label className="text-xs">Tab "Tutti" (default: "Tutti i Fornitori")</Label>
                    <Input value={tenantConfig.custom_labels?.directory_tab_all || ''} onChange={(e) => updateTenantLabel('directory_tab_all', e.target.value)} placeholder="Tutti i Fornitori" />
                  </div>
                  <div>
                    <Label className="text-xs">Tab "I Miei" (default: "I Miei Fornitori")</Label>
                    <Input value={tenantConfig.custom_labels?.directory_tab_mine || ''} onChange={(e) => updateTenantLabel('directory_tab_mine', e.target.value)} placeholder="I Miei Fornitori" />
                  </div>
                  <div>
                    <Label className="text-xs">Tab "In Attesa" (default: "In Attesa")</Label>
                    <Input value={tenantConfig.custom_labels?.directory_tab_pending || ''} onChange={(e) => updateTenantLabel('directory_tab_pending', e.target.value)} placeholder="In Attesa" />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTenantConfigDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveTenantConfig} disabled={savingTenantConfig}>
              {savingTenantConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva Configurazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AdminAssociationsPage;
